import React, { useState } from "react";
import styled from "styled-components";

import Modal from "../modal/Modal";
// @ts-expect-error - JSX ファイルの型定義なし
import LanguageSwitcher from "../tab/LanguageSwitcher";
import Input from "../form-item/Input";

import OfferContractorAvatars, {
  type OfferContractor,
} from "./OfferContractorAvatars";

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FieldGroup = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SectionTitle = styled.div`
  width: 100%;
  border-top: 1px dashed var(--soremo-border);
  padding-top: 12px;
`;

const ErrorBox = styled.div`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid var(--soremo-green);
  border-radius: 4px;
  color: var(--soremo-green);
  white-space: pre-wrap;
  word-break: break-word;
`;

export interface EntryFormResult {
  role: string;
  role_en: string;
  display_name: string;
  display_name_en: string;
  link_url: string;
  linked_user_id: number | null;
}

export interface EntryFormModalProps {
  projectId: string;
  mode?: "create" | "edit";
  initialValues?: Partial<EntryFormResult>;
  onSubmit: (result: EntryFormResult) => void;
  onClose: () => void;
  isSubmitting?: boolean;
  submitError?: string | null;
}

const EntryFormModal: React.FC<EntryFormModalProps> = ({
  projectId,
  mode = "create",
  initialValues,
  onSubmit,
  onClose,
  isSubmitting = false,
  submitError = null,
}) => {
  const [language, setLanguage] = useState<"JP" | "EN">("JP");
  const [role, setRole] = useState(initialValues?.role ?? "");
  const [roleEn, setRoleEn] = useState(initialValues?.role_en ?? "");
  const [displayName, setDisplayName] = useState(
    initialValues?.display_name ?? "",
  );
  const [displayNameEn, setDisplayNameEn] = useState(
    initialValues?.display_name_en ?? "",
  );
  const [linkUrl, setLinkUrl] = useState(initialValues?.link_url ?? "");
  const [linkedUserId, setLinkedUserId] = useState<number | null>(
    initialValues?.linked_user_id ?? null,
  );
  const [selectedContractorKey, setSelectedContractorKey] = useState<
    string | null
  >(null);

  const isValid =
    role.trim().length > 0 ||
    roleEn.trim().length > 0 ||
    displayName.trim().length > 0 ||
    displayNameEn.trim().length > 0;

  const handleSelectContractor = (c: OfferContractor) => {
    setSelectedContractorKey(c.key);
    setLinkedUserId(c.user_id);
    // クリック都度、JP/EN 両方の表示名とロールを上書き反映する。
    setDisplayName(c.display_name);
    setDisplayNameEn(c.display_name_en);
    setRole(c.role);
    setRoleEn(c.role);
  };

  const handleSubmit = () => {
    if (!isValid || isSubmitting) return;
    onSubmit({
      role,
      role_en: roleEn,
      display_name: displayName,
      display_name_en: displayNameEn,
      link_url: linkUrl,
      linked_user_id: linkedUserId,
    });
  };

  const isEdit = mode === "edit";
  const title = isEdit ? "エントリーを編集" : "エントリーを追加";
  const submitLabel = isEdit
    ? isSubmitting ? "保存中..." : "保存"
    : isSubmitting ? "追加中..." : "OK";

  return (
    <Modal
      onClose={onClose}
      title={title}
      description={
        isEdit
          ? "クレジットの内容を編集します。"
          : "アイコンをクリックすると、ロールと表示名が自動入力されます。"
      }
      primaryAction={{
        label: submitLabel,
        onClick: handleSubmit,
        disabled: !isValid || isSubmitting,
      }}
      secondaryAction={{
        label: "キャンセル",
        onClick: onClose,
        disabled: isSubmitting,
      }}
    >
      <Container>
        {submitError && (
          <ErrorBox className="bodytext-13">{submitError}</ErrorBox>
        )}
        {!isEdit && (
          <OfferContractorAvatars
            projectId={projectId}
            selectedKey={selectedContractorKey}
            onSelect={handleSelectContractor}
          />
        )}
        <SectionTitle className="bodytext-11 u-text-light-gray">
          クレジット表記
        </SectionTitle>
        <FieldGroup>
          <Input
            label={language === "JP" ? "ロール" : "Role"}
            option="optional"
            type="text"
            placeholder={language === "JP" ? "ディレクター" : "Director"}
            width="100%"
            name={language === "JP" ? "role" : "role_en"}
            value={language === "JP" ? role : roleEn}
            onChange={(e) =>
              language === "JP"
                ? setRole(e.target.value)
                : setRoleEn(e.target.value)
            }
          />
          <Input
            label={language === "JP" ? "表示名" : "Display Name"}
            option="optional"
            type="text"
            placeholder={language === "JP" ? "山田 太郎" : "Taro Yamada"}
            width="100%"
            name={language === "JP" ? "display_name" : "display_name_en"}
            value={language === "JP" ? displayName : displayNameEn}
            onChange={(e) =>
              language === "JP"
                ? setDisplayName(e.target.value)
                : setDisplayNameEn(e.target.value)
            }
          />
          <Input
            label="外部リンク"
            option="optional"
            type="text"
            placeholder="https://example.com/profile"
            width="100%"
            name="link_url"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
          />
        </FieldGroup>
        <LanguageSwitcher onLanguageChange={setLanguage} />
      </Container>
    </Modal>
  );
};

export default EntryFormModal;
